import React, { useEffect, useState } from "react"
import classNames from "classnames"
import { NewTable } from "@tenforce/toolbox-table-react"
import {
    Action,
    ActionAnchor,
    ActionType,
} from "@tenforce/toolbox-button-push-react"
import { BaseInputField } from "@tenforce/product-fields"

import { IItemPickerField } from "../../.."
import { useAppDispatch, useAppSelector } from "../../../../../hooks"
import { GlobalState } from "../../../../../store"
import HTMLAndInternalLinkParser from "../../common/HTMLAndInternalLinkParser"
import { Exceptions, Labels } from "../../../../../util/Translations"
import { updateField } from "../../../slice"
import Ancestry from "./Ancestry"
import { getItemDetailUrl } from "../../../../../util/UrlGenerator"
import env from "../../../../../env"
import { ITenForceWindow } from "../../../../../global"
import { addErrorNotification } from "../../../../notifications/actions"

type Props = IItemPickerField

declare let window: ITenForceWindow

const ItemPicker: React.FC<Props> = (props: Props) => {
    const dispatch = useAppDispatch()
    const {
        title,
        description,
        isRequired,
        isDisabled,
        isReadOnly,
        itemId,
        fieldId,
        isSingleSelect,
        value: selectedItems,
    } = props

    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [hasRecentlyFailedToUpdate, setHasRecentlyFailedToUpdate] =
        useState<boolean>(false)
    const [hasRecentlyBeenUpdated, setHasRecentlyBeenUpdated] =
        useState<boolean>(false)
    const { isLoading, currentFieldInEditMode = "" } = useAppSelector(
        (state: GlobalState) => state.fields,
    )

    const {
        ItemPickerAddItem,
        ItemPickerEditItem,
        ItemPickerEditItems,
        ItemPickerAddItems,
    } = Labels
    const { Error: ExcError } = Exceptions

    useEffect(() => {
        if (hasRecentlyFailedToUpdate) {
            setTimeout(() => {
                setHasRecentlyFailedToUpdate(false)
            }, 3000)
        }
    }, [hasRecentlyFailedToUpdate])

    useEffect(() => {
        if (hasRecentlyBeenUpdated) {
            setTimeout(() => {
                setHasRecentlyBeenUpdated(false)
            }, 3000)
        }
    }, [hasRecentlyBeenUpdated])

    const isFieldDisabled =
        isDisabled || !["", fieldId].includes(currentFieldInEditMode)

    const dataFtAttributes = {
        "data-ft-is-saving": isSaving,
        "data-ft-is-loading": isLoading,
        "data-ft-is-readonly": isReadOnly,
        "data-ft-is-required": isRequired,
        "data-ft-is-saved": hasRecentlyBeenUpdated,
        "data-ft-is-saved-failed": hasRecentlyFailedToUpdate,
    }

    const finalClasses = classNames(
        "tw-w-full",
        { "is-saving": isSaving },
        { "is-saved": hasRecentlyBeenUpdated },
        { "is-saved-failed": hasRecentlyFailedToUpdate },
        { "is-loading": isLoading },
        { "is-disabled": isFieldDisabled },
    )

    const anchorFinalClass = classNames(
        "text-action tw-no-underline tw-text-black toolbox-cover-action",
        { "is-loading": isLoading },
    )

    return (
        <BaseInputField
            title={title}
            description={
                <HTMLAndInternalLinkParser
                    content={(description as string) ?? ""}
                />
            }
            data-field-id={fieldId}
            extraClasses={finalClasses}
            isLoading={isLoading}
            isRequired={isRequired && selectedItems.length === 0}
            {...dataFtAttributes}
        >
            <NewTable.Table>
                <NewTable.Body>
                    {selectedItems?.map((item) => {
                        return (
                            <NewTable.Row key={item.id} extraClasses="bg-cloud">
                                <NewTable.DataCell>
                                    <Ancestry id={item.id} />
                                    <ActionAnchor
                                        isLabel={false}
                                        href={getItemDetailUrl(
                                            env.applicationPath,
                                            "",
                                            item.id,
                                        )}
                                        className={anchorFinalClass}
                                        data-ft-option-label={item.title}
                                        data-ft-option-id={item.id}
                                    >
                                        {item.title}
                                    </ActionAnchor>
                                </NewTable.DataCell>
                            </NewTable.Row>
                        )
                    })}
                    {!isReadOnly && (
                        <NewTable.Row className="">
                            <NewTable.DataCell extraClasses="tw-flex">
                                <Action
                                    isDisabled={isDisabled}
                                    isLoading={isLoading}
                                    isReadOnly={false}
                                    isLabel={false}
                                    actionType={ActionType.action}
                                    isTransparent={false}
                                    isSecondary={false}
                                    extraClasses="tw-w-full theme-cni"
                                    onClick={(): void => {
                                        const ovl = new window.Overlay(
                                            window.Overlay.topmost,
                                        ).setCallback(
                                            async function (
                                                selectedIdsAsStrings: string[],
                                            ): Promise<void> {
                                                setIsSaving(true)
                                                try {
                                                    const selectedIds =
                                                        selectedIdsAsStrings.map(
                                                            (id: string) =>
                                                                parseInt(id),
                                                        )
                                                    await dispatch(
                                                        updateField({
                                                            field: props,
                                                            newValue:
                                                                selectedIds,
                                                        }),
                                                    ).unwrap()
                                                    ovl.remove()

                                                    setHasRecentlyBeenUpdated(
                                                        true,
                                                    )
                                                } catch (error) {
                                                    console.error(error)
                                                    setHasRecentlyFailedToUpdate(
                                                        true,
                                                    )
                                                } finally {
                                                    setIsSaving(false)
                                                    ovl.wrapper
                                                        .down(
                                                            ".cnio-footer a.save",
                                                        )
                                                        .hideSpinner()
                                                }
                                            }.bind(this),
                                            () => {
                                                ovl.remove()
                                                dispatch(
                                                    addErrorNotification(
                                                        ExcError,
                                                    ),
                                                )
                                            },
                                        )

                                        ovl.load(
                                            window.TenForce.Execution.Web.Ajax
                                                .Service
                                                .ShowMultilevelSelectOverlay,
                                            window.Overlay.instanceCounter,
                                            itemId,
                                            "",
                                            fieldId.replace("CF", ""),
                                            "0",
                                            0,
                                            selectedItems
                                                .map(
                                                    (selectedItem) =>
                                                        selectedItem.id,
                                                )
                                                .join(","),
                                            !isSingleSelect,
                                            null,
                                            null,
                                        )
                                    }}
                                >
                                    {selectedItems.length > 0 &&
                                        isSingleSelect &&
                                        ItemPickerEditItem}
                                    {selectedItems.length > 0 &&
                                        !isSingleSelect &&
                                        ItemPickerEditItems}
                                    {selectedItems.length === 0 &&
                                        isSingleSelect &&
                                        ItemPickerAddItem}
                                    {selectedItems.length === 0 &&
                                        !isSingleSelect &&
                                        ItemPickerAddItems}
                                </Action>
                            </NewTable.DataCell>
                        </NewTable.Row>
                    )}
                </NewTable.Body>
            </NewTable.Table>
        </BaseInputField>
    )
}

export default ItemPicker
